import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";
import { Calculator, Plus, Minus, ArrowRight } from "lucide-react";
import useToolPageCms from "../hooks/useToolPageCms";
import { ToolPageBanner, ToolPageCtaBand } from "../components/ToolPageCms";

const FALLBACK_CMS = {
  page_banner: {
    eyebrow: "Volumemeter",
    title: "Bereken uw",
    highlight: "totale volume",
    subtitle:
      "Selecteer hoeveel u verstuurt — we berekenen direct het totale volume in m³ voor verhuizing, dozen of zakelijke cargo.",
    badges: [
      { icon: "package", label: "Dozen, meubels, apparaten" },
      { icon: "truck",   label: "Verhuizing-vriendelijk" },
      { icon: "info",    label: "Tellingen → m³ direct" },
    ],
  },
  intro: {},
  cta_band: {
    title: "Volume bepaald — vraag uw verhuisofferte",
    description: "Op basis van uw geschatte m³ geven wij een vrijblijvende indicatie.",
    cta_primary_label: "Verhuisofferte aanvragen",
    cta_primary_url: "/contact?get-quote=1#quote-form",
    cta_secondary_label: "Bel voor advies",
    cta_secondary_url: "tel:+31488235999",
  },
};

/* ----------------------------------------------------------------------
 *  Volume presets. CBM = (L × W × H) in metres, multiplied by quantity.
 *  Numbers based on widely-published averages for moving estimation.
 * ---------------------------------------------------------------------- */

const PRESETS = [
  { group: "Dozen", items: [
    { key: "doos-60",    label: "Verhuisdoos 60L",  cbm: 0.06 },
    { key: "doos-80",    label: "Verhuisdoos 80L",  cbm: 0.08 },
    { key: "doos-120",   label: "Verhuisdoos 120L", cbm: 0.12 },
    { key: "doos-160",   label: "Verhuisdoos 160L", cbm: 0.16 },
    { key: "doos-240",   label: "Verhuisdoos 240L", cbm: 0.24 },
    { key: "doos-270",   label: "Verhuisdoos 270L", cbm: 0.27 },
  ]},
  { group: "Woonkamer", items: [
    { key: "bank-2",     label: "Bank 2-zits",     cbm: 1.2 },
    { key: "bank-3",     label: "Bank 3-zits",     cbm: 1.6 },
    { key: "stoel",      label: "Stoel",           cbm: 0.4 },
    { key: "eettafel",   label: "Eettafel",        cbm: 0.9 },
    { key: "kast-kl",    label: "Kast klein",      cbm: 0.7 },
    { key: "kast-gr",    label: "Kast groot",      cbm: 1.5 },
  ]},
  { group: "Slaapkamer", items: [
    { key: "bed-1p",     label: "Bed 1-persoons",  cbm: 1.0 },
    { key: "bed-2p",     label: "Bed 2-persoons",  cbm: 1.8 },
  ]},
  { group: "Apparaten", items: [
    { key: "koelkast",   label: "Koelkast",        cbm: 0.7 },
    { key: "wasmachine", label: "Wasmachine",      cbm: 0.4 },
    { key: "droger",     label: "Droger",          cbm: 0.4 },
  ]},
  { group: "Overig", items: [
    { key: "fiets",      label: "Fiets",           cbm: 0.4 },
  ]},
];

function categorise(total) {
  if (total < 0.5) return { label: "Tot 0.5 m³", color: "bg-blue-100 text-blue-800" };
  if (total < 1)   return { label: "0.5 tot 1 m³", color: "bg-indigo-100 text-indigo-800" };
  if (total < 3)   return { label: "1 tot 3 m³", color: "bg-purple-100 text-purple-800" };
  if (total < 6)   return { label: "3 tot 6 m³", color: "bg-amber-100 text-amber-800" };
  return { label: "6+ m³", color: "bg-red-100 text-red-800" };
}

export default function VolumemeterPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState({});
  const { data: cms } = useToolPageCms("volumemeter", FALLBACK_CMS);

  const setCount = (key, n) =>
    setCounts((s) => ({ ...s, [key]: Math.max(0, n) }));

  const { totalCbm, totalItems } = useMemo(() => {
    let cbm = 0, items = 0;
    for (const group of PRESETS) {
      for (const it of group.items) {
        const n = counts[it.key] || 0;
        cbm += it.cbm * n;
        items += n;
      }
    }
    return { totalCbm: Math.round(cbm * 100) / 100, totalItems: items };
  }, [counts]);

  const cat = categorise(totalCbm);

  const goQuote = () => {
    const summary = PRESETS.flatMap((g) =>
      g.items.filter((it) => counts[it.key] > 0).map((it) => `${counts[it.key]}× ${it.label}`)
    ).join(", ");
    const note = `Volumemeter resultaat: ${totalCbm} m³ (${totalItems} items)${summary ? " — " + summary : ""}`;
    navigate(`/contact?get-quote=1&note=${encodeURIComponent(note)}`);
  };

  return (
    <>
      <SEO
        title="Volumemeter voor Luchtvracht — Bereken volume van uw zending | 5999Cargo"
        description="Bereken snel het totale volume (m³) en volumetrisch gewicht van uw pakket of vracht voor luchtvracht en verhuiszendingen naar Curaçao en de Caribbean."
        keywords="volumemeter luchtvracht, volumetrisch gewicht berekenen, m3 verhuiscontainer Curaçao, volume berekenen Caribbean"
      />
      <main className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50" data-testid="volumemeter-page">
        <ToolPageBanner
          banner={cms?.page_banner || FALLBACK_CMS.page_banner}
          image="https://images.unsplash.com/photo-1773125929765-99d4d67e831d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          imageAlt="Verhuisdozen klaar voor transport — volume berekenen"
          eyebrowIcon={Calculator}
          testid="volumemeter-hero"
        />

        <section className="py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {PRESETS.map((group) => (
                <div key={group.group} className="card-premium overflow-hidden" data-testid={`volume-group-${group.group}`}>
                  <div className="px-5 py-3 border-b border-slate-100 font-chivo font-bold text-xs uppercase tracking-[0.18em] text-brand-500 bg-slate-50/60">{group.group}</div>
                  <div className="divide-y divide-slate-100">
                    {group.items.map((it) => {
                      const n = counts[it.key] || 0;
                      return (
                        <div key={it.key} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-slate-50/50 transition-colors" data-testid={`volume-row-${it.key}`}>
                          <div className="min-w-0">
                            <div className="text-sm font-chivo font-bold text-brand-900">{it.label}</div>
                            <div className="text-[11px] text-ink-400 font-mono-num">{it.cbm} m³ per stuk</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="icon" variant="outline" className="rounded-lg h-9 w-9 border-slate-200" onClick={() => setCount(it.key, n - 1)} disabled={n === 0} data-testid={`volume-dec-${it.key}`}><Minus className="w-3.5 h-3.5" /></Button>
                            <span className="font-mono-num font-bold w-8 text-center text-brand-900" data-testid={`volume-count-${it.key}`}>{n}</span>
                            <Button size="icon" variant="outline" className="rounded-lg h-9 w-9 border-slate-200" onClick={() => setCount(it.key, n + 1)} data-testid={`volume-inc-${it.key}`}><Plus className="w-3.5 h-3.5" /></Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Result panel */}
            <aside className="card-dark p-7 lg:sticky lg:top-24 self-start relative overflow-hidden" data-testid="volume-result-panel">
              <div className="absolute -top-20 -right-12 w-56 h-56 rounded-full bg-brand-500/30 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="text-[11px] uppercase tracking-[0.18em] text-brand-300 font-chivo font-bold">Totaal</div>
                <div className="font-chivo font-black text-5xl mt-3 font-mono-num" data-testid="volume-total-cbm">{totalCbm} m³</div>
                <div className="text-sm text-white/75 mt-1.5" data-testid="volume-total-items">{totalItems} items</div>
                <div className="mt-5">
                  <span className={`inline-flex items-center text-xs uppercase tracking-wider font-chivo font-bold px-3 py-1.5 rounded-full ${cat.color}`} data-testid="volume-category">
                    {cat.label}
                  </span>
                </div>
                <Button onClick={goQuote} className="mt-7 w-full rounded-xl bg-white text-brand-900 hover:bg-brand-50 font-chivo font-bold uppercase text-sm shadow-brand-card transition-all" data-testid="volume-cta-quote">
                  Vraag offerte aan <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-[11px] text-white/60 mt-4 leading-relaxed">
                  Indicatie. Definitieve volume wordt bepaald bij ophaling/inmeting.
                </p>
              </div>
            </aside>
          </div>
        </section>
        <ToolPageCtaBand cta={cms?.cta_band || FALLBACK_CMS.cta_band} testid="volumemeter-cta-band" />
      </main>
    </>
  );
}
